import { Link } from "react-router";

import { BotonDescargarCotizacion } from "@/components/informes/BotonDescargarCotizacion";
import { Button } from "@/components/ui/button";
import type { Cotizacion, Informe } from "@/lib/informes";
import { urlDeLaEdicion } from "@/lib/origenDeNavegacion";

import { describirEntrega } from "@/lib/fechas";

/**
 * Confirmación del correo generado (RF-F3, pantalla `19 UN`).
 *
 * Muestra el correo que se generó, NO uno que ya salió: el envío real llega con
 * el módulo de correos, que sigue diferido. Decir "se envió" sería prometer
 * algo que no ocurrió, así que el texto habla de un correo generado.
 *
 * El PDF sí se puede mirar: "Ver PDF de cotización" abre la VISTA PREVIA del
 * documento, dibujada con los datos ya persistidos. El archivo definitivo se
 * emite cuando el revisor aprueba y envía la cotización al cliente (Módulo G).
 *
 * Vive acá y no dentro de `CotizarInforme` porque dejó de ser un momento del
 * formulario: tiene pantalla propia (`/informes/:id/cotizacion/enviada`) y se
 * vuelve a ella desde el documento.
 */
export function ConfirmacionDeEnvio({
  informe,
  cotizacion,
}: {
  informe: Informe;
  cotizacion: Cotizacion;
}) {
  const revisores = cotizacion.revisores ?? [];
  const nombres = listaLegible(revisores.map((r) => r.nombre));

  return (
    <>
      <div>
        <h2 className="text-h2 text-ink">Cotización enviada a revisión</h2>
        <p className="text-ink-muted mt-1 text-sm">
          {revisores.length > 0
            ? `Se generó un correo de revisión con el PDF de la cotización, dirigido a ${nombres}.`
            : "Se generó el correo de revisión, pero este informe todavía no tiene revisores asignados."}
        </p>
      </div>

      {/* Vista previa del correo. Es una MAQUETA del contenido generado, no un
          registro de envío: se dibuja con el mismo dato que se persistió. */}
      <article className="bg-card max-w-[70ch] overflow-hidden rounded-xl border">
        <header className="bg-primary-foreground/5 bg-ink px-6 py-5">
          <p className="text-xs font-bold tracking-widest text-white">CONSULTEK</p>
          <p className="mt-0.5 text-xs tracking-widest text-white/70">
            SOLICITUD DE VISADO DE COTIZACIÓN
          </p>
        </header>

        <div className="space-y-4 p-6">
          {revisores.length > 0 ? (
            <p className="text-ink-hint text-xs">
              Para: {nombres} (
              <span className="font-mono">{revisores.map((r) => r.email).join(", ")}</span>)
            </p>
          ) : null}

          <h3 className="text-h3 text-ink">
            Cotización pendiente de visado: {informe.propiedad_nombre ?? "—"}
          </h3>

          <p className="text-ink-body text-sm">
            Se generó una cotización de{" "}
            <strong className="font-mono">{cotizacion.precio_uf} UF</strong> con entrega{" "}
            <strong className="font-mono">{describirEntrega(cotizacion.fecha_entrega, cotizacion.plazo_dias_habiles)}</strong> para
            el cliente <strong>{informe.cliente_nombre ?? "—"}</strong>. Por favor revisa el PDF
            adjunto y confirma si está correcta.
          </p>

          <div className="flex flex-wrap gap-3">
            <BotonDescargarCotizacion informeId={informe.id} variant="default" />
            <Button asChild variant="outline">
              <Link to={urlDeLaEdicion(informe.id, "cotizacion")}>Editar datos del informe</Link>
            </Button>
          </div>

          <p className="text-ink-hint text-xs">
            El revisor puede aprobar y enviarla al cliente desde su bandeja de revisiones
            pendientes.
          </p>
        </div>
      </article>

      <p className="text-ink-hint text-xs">
        El correo todavía no se envía: el envío llega con el módulo de correos. El PDF definitivo
        se genera cuando el revisor aprueba la cotización y la envía al cliente.
      </p>
    </>
  );
}

/** "Ana", "Ana y Beto", "Ana, Beto y Carla". */
function listaLegible(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? "";
  return `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
}
